const React = require('react');
const { createElement: h, useState, useEffect } = React;
const { request } = require('../lib/apiClient');
require('./SchedulerPage.css');

var FREQ_OPTIONS = [
  { value: 'once', label: 'Once' },
  { value: 'minutely', label: 'Every minute' },
  { value: 'custom', label: 'Custom (every N minutes)' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

var FREQ_LABEL = {};
FREQ_OPTIONS.forEach(function(o) { FREQ_LABEL[o.value] = o.label; });

function describeFrequency(schedule) {
  if (schedule.frequency === 'custom') {
    var n = Number(schedule.customMinutes);
    if (Number.isFinite(n) && n > 0) {
      return n === 1 ? 'Every minute' : 'Every ' + n + ' minutes';
    }
    return 'Custom';
  }
  return FREQ_LABEL[schedule.frequency] || schedule.frequency;
}

function formatNextRun(schedule) {
  if (!schedule.enabled) return 'Paused';
  var now = new Date();
  var next = new Date(schedule.nextRunAt);
  if (!Number.isFinite(next.getTime())) return '-';
  var diff = next - now;
  if (diff < 0) return 'Overdue';
  if (diff < 60000) return 'In less than a minute';
  if (diff < 3600000) return 'In ' + Math.round(diff / 60000) + ' min';
  if (diff < 86400000) return 'In ' + Math.round(diff / 3600000) + ' hr';
  return next.toLocaleDateString();
}

function SchedulerPage(props) {
  var workflows = Array.isArray(props && props.workflows) ? props.workflows : [];
  // Browser workflows run in the user's Chrome via the Browser Action
  // extension — they can't run in the background, so no scheduling.
  var schedulableWorkflows = workflows.filter(function(wf) { return !(wf && wf.hasBrowserNode); });
  var [schedules, setSchedules] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showForm, setShowForm] = useState(false);
  var [form, setForm] = useState({ workflowId: '', frequency: 'daily', customMinutes: '5', startAt: '' });
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState('');

  useEffect(function() {
    setLoading(true);
    request('/schedules')
      .then(function(res) {
        setSchedules(Array.isArray(res && res.data) ? res.data : []);
      })
      .catch(function() {
        setError('Failed to load schedules.');
      })
      .finally(function() {
        setLoading(false);
      });
  }, []);

  function handleCreate() {
    if (!form.workflowId) return;
    var customMinutes = null;
    if (form.frequency === 'custom') {
      customMinutes = Math.floor(Number(form.customMinutes));
      if (!Number.isFinite(customMinutes) || customMinutes < 1) {
        setError('Enter a whole number of minutes (1 or more).');
        return;
      }
    }
    setSaving(true);
    var wf = workflows.find(function(w) { return String(w.id) === form.workflowId; });
    var body = {
      workflowId: form.workflowId,
      workflowName: wf ? (wf.workflowName || 'Untitled') : 'Unknown',
      frequency: form.frequency,
      startAt: form.startAt ? new Date(form.startAt).toISOString() : new Date().toISOString()
    };
    if (customMinutes) body.customMinutes = customMinutes;
    request('/schedules', { method: 'POST', body: body })
      .then(function(res) {
        if (res && res.data) {
          setSchedules(function(prev) { return [res.data].concat(prev); });
        }
        setForm({ workflowId: '', frequency: 'daily', customMinutes: '5', startAt: '' });
        setShowForm(false);
      })
      .catch(function() {
        setError('Failed to create schedule.');
      })
      .finally(function() {
        setSaving(false);
      });
  }

  function handleToggle(id) {
    request('/schedules/' + id + '/toggle', { method: 'PATCH' })
      .then(function(res) {
        if (res && res.data) {
          setSchedules(function(prev) {
            return prev.map(function(s) { return String(s._id) === String(id) ? res.data : s; });
          });
        }
      })
      .catch(function() {
        setError('Failed to update schedule.');
      });
  }

  function handleDelete(id) {
    request('/schedules/' + id, { method: 'DELETE' })
      .then(function() {
        setSchedules(function(prev) { return prev.filter(function(s) { return String(s._id) !== String(id); }); });
      })
      .catch(function() {
        setError('Failed to delete schedule.');
      });
  }

  return h('div', { className: 'sch-root' },
    h('header', { className: 'sch-topbar' },
      h('h1', { className: 'sch-page-title' }, 'Scheduler'),
      h('button', {
        className: 'sch-create-btn',
        onClick: function() { setShowForm(true); setError(''); }
      },
        h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
          h('path', { d: 'M12 5v14M5 12h14' })
        ),
        'New Schedule'
      )
    ),

    error
      ? h('div', { className: 'sch-error' }, error)
      : null,

    showForm
      ? h('div', { className: 'sch-modal-overlay', onClick: function() { setShowForm(false); } },
          h('div', { className: 'sch-modal', onClick: function(e) { e.stopPropagation(); } },
            h('h2', { className: 'sch-modal-title' }, 'New Schedule'),
            h('div', { className: 'sch-field' },
              h('label', { className: 'sch-label' }, 'Workflow'),
              h('select', {
                className: 'sch-select',
                value: form.workflowId,
                onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { workflowId: e.target.value }); }); }
              },
                h('option', { value: '' }, '— Select a workflow —'),
                schedulableWorkflows.map(function(wf) {
                  return h('option', { key: wf.id, value: String(wf.id) }, wf.workflowName || 'Untitled');
                })
              ),
              workflows.length > schedulableWorkflows.length
                ? h('p', {
                    style: { fontSize: 12, color: '#8b90a8', margin: '6px 0 0' }
                  }, 'Browser workflows aren’t listed — they run in your Chrome and can’t be scheduled.')
                : null
            ),
            h('div', { className: 'sch-field' },
              h('label', { className: 'sch-label' }, 'Frequency'),
              h('select', {
                className: 'sch-select',
                value: form.frequency,
                onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { frequency: e.target.value }); }); }
              },
                FREQ_OPTIONS.map(function(o) {
                  return h('option', { key: o.value, value: o.value }, o.label);
                })
              )
            ),
            form.frequency === 'custom'
              ? h('div', { className: 'sch-field' },
                  h('label', { className: 'sch-label' }, 'Run every (minutes)'),
                  h('input', {
                    className: 'sch-input',
                    type: 'number',
                    min: 1,
                    step: 1,
                    value: form.customMinutes,
                    onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { customMinutes: e.target.value }); }); }
                  })
                )
              : null,
            h('div', { className: 'sch-field' },
              h('label', { className: 'sch-label' }, 'Start at'),
              h('input', {
                className: 'sch-input',
                type: 'datetime-local',
                value: form.startAt,
                onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { startAt: e.target.value }); }); }
              })
            ),
            h('div', { className: 'sch-modal-actions' },
              h('button', { className: 'sch-cancel-btn', onClick: function() { setShowForm(false); } }, 'Cancel'),
              h('button', {
                className: 'sch-confirm-btn',
                disabled: !form.workflowId || saving,
                onClick: handleCreate
              }, saving ? 'Saving...' : 'Create')
            )
          )
        )
      : null,

    h('div', { className: 'sch-table-wrap' },
      h('div', { className: 'sch-table-header' },
        h('span', { className: 'sch-table-label' }, 'Scheduled Runs'),
        h('span', { className: 'sch-table-count' }, String(schedules.length) + ' total')
      ),
      loading
        ? h('div', { className: 'sch-empty' }, 'Loading...')
        : schedules.length === 0
          ? h('div', { className: 'sch-empty' },
              h('svg', { width: 40, height: 40, viewBox: '0 0 24 24', fill: 'none', stroke: '#d1d5db', strokeWidth: 1.5 },
                h('circle', { cx: 12, cy: 12, r: 9 }),
                h('path', { d: 'M12 7v5l3 3' })
              ),
              h('p', null, 'No schedules yet. Create one to automate workflow runs.')
            )
          : h('table', { className: 'sch-table' },
              h('thead', null,
                h('tr', null,
                  h('th', null, 'Workflow'),
                  h('th', null, 'Frequency'),
                  h('th', null, 'Next Run'),
                  h('th', null, 'Last Run'),
                  h('th', null, 'Status'),
                  h('th', null, '')
                )
              ),
              h('tbody', null,
                schedules.map(function(s) {
                  var id = String(s._id || s.id || '');
                  return h('tr', { key: id, className: 'sch-table-row' },
                    h('td', { className: 'sch-cell-name' },
                      h('div', { className: 'sch-wf-icon' },
                        h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
                          h('circle', { cx: 12, cy: 12, r: 9 }),
                          h('path', { d: 'M12 7v5l3 3' })
                        )
                      ),
                      s.workflowName || 'Untitled'
                    ),
                    h('td', null, describeFrequency(s)),
                    h('td', null, formatNextRun(s)),
                    h('td', null,
                      s.lastRunAt
                        ? h('span', null,
                            new Date(s.lastRunAt).toLocaleDateString() + ' ',
                            h('span', {
                              className: 'sch-run-status sch-run-status-' + (s.lastRunStatus || 'pending')
                            }, s.lastRunStatus || '')
                          )
                        : h('span', { className: 'sch-muted' }, 'Never')
                    ),
                    h('td', null,
                      h('span', { className: 'sch-badge' + (s.enabled ? ' sch-badge-active' : ' sch-badge-paused') },
                        s.enabled ? 'Active' : 'Paused'
                      )
                    ),
                    h('td', { className: 'sch-cell-actions' },
                      h('div', { className: 'sch-row-actions' },
                        h('button', {
                          className: 'sch-toggle-btn',
                          onClick: function() { handleToggle(id); }
                        }, s.enabled ? 'Pause' : 'Resume'),
                        h('button', {
                          className: 'sch-delete-btn',
                          onClick: function() { handleDelete(id); }
                        },
                          h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                            h('polyline', { points: '3 6 5 6 21 6' }),
                            h('path', { d: 'M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6' }),
                            h('path', { d: 'M10 11v6M14 11v6' }),
                            h('path', { d: 'M9 6V4h6v2' })
                          )
                        )
                      )
                    )
                  );
                })
              )
            )
    )
  );
}

module.exports = SchedulerPage;
